import {
  ChevronLeft,
  ChevronRight,
  Compass,
  Download,
  Eye,
  Filter,
  FolderHeart,
  Grid2X2,
  Heart,
  Menu,
  Moon,
  Search,
  Sparkles,
  Sun,
  Trophy,
  Upload,
  UserRound,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "./components/LanguageSwitcher";
import { ExternalWorkshop } from "./components/ExternalWorkshop";
import { GamePage } from "./components/GamePage";
import { DiscoverHighlights } from "./components/DiscoverHighlights";
import { DownloadPage } from "./components/DownloadPage";
import { PetDetailPage } from "./components/PetDetailPage";
import { PetInspector } from "./components/PetInspector";
import { RankPage } from "./components/RankPage";
import { SeoContentPage } from "./components/SeoContentPage";
import { UploadPage } from "./components/UploadPage";
import { allCollections, allTags, pets, type Pet } from "./data/pets";
import { getSeoContentPage, SEO_CONTENT_PAGES, type SeoContentPage as SeoContentPageData } from "./data/seo-content";
import type { SortMode } from "./lib/catalog";
import { fetchCatalog, type CatalogPet } from "./lib/catalog-client";
import type { CatalogResponse, CatalogSort } from "./lib/catalog-feed";
import { downloadPetArchive } from "./lib/download";
import { ENGINE_NAV_ITEMS, getViewPath, resolveEngineRoute, toRouteSlug, type EngineView } from "./lib/navigation";
import { mirroredShareDataToPet, type MirroredShareData } from "./lib/mirrored-pets";
import { petSvgDataUri } from "./lib/pet-art";

type View = EngineView;
type Theme = "light" | "dark";

const PAGE_SIZE = 12;

const sortOptions: Array<{ value: SortMode; label: string }> = [
  { value: "newest", label: "Newest" },
  { value: "liked", label: "Liked" },
  { value: "viewed", label: "Viewed" },
  { value: "downloaded", label: "Downloaded" },
  { value: "random", label: "Random" },
];

const navItems: Array<{ view: View; label: string; icon: typeof Grid2X2 }> = [
  { view: "gallery", label: "Discover", icon: Grid2X2 },
  { view: "collections", label: "Collections", icon: FolderHeart },
  { view: "creators", label: "Workshop", icon: UserRound },
  { view: "upload", label: "My Library", icon: Upload },
  { view: "rank", label: "Rank", icon: Trophy },
  { view: "download", label: "Download", icon: Download },
];

function readStoredSet(key: string): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(key) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

function useLocalSet(key: string) {
  const [values, setValues] = useState<Set<string>>(() => readStoredSet(key));

  const toggle = (value: string) => {
    setValues((current) => {
      const next = new Set(current);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      localStorage.setItem(key, JSON.stringify([...next]));
      return next;
    });
  };

  const add = (value: string) => {
    setValues((current) => {
      if (current.has(value)) return current;
      const next = new Set(current).add(value);
      localStorage.setItem(key, JSON.stringify([...next]));
      return next;
    });
  };

  return { values, toggle, add };
}

function formatCount(value: number): string {
  if (value >= 1000) return `${(value / 1000).toFixed(value >= 10_000 ? 0 : 1)}k`;
  return value.toString();
}

function App() {
  const { t } = useTranslation();
  const [view, setView] = useState<View>("gallery");
  const [query, setQuery] = useState("");
  const [sortMode, setSortMode] = useState<SortMode>("newest");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<string | undefined>();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [routePet, setRoutePet] = useState<Pet | null>(null);
  const [contentPage, setContentPage] = useState<SeoContentPageData | null>(null);
  const [inspectorPet, setInspectorPet] = useState<Pet | null>(null);
  const [rankSub, setRankSub] = useState<"connect" | "me" | undefined>();
  const [page, setPage] = useState(1);
  const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem("cpl-theme-v2") as Theme | null) ?? "dark");
  const liked = useLocalSet("cpl-liked-pets");
  const viewed = useLocalSet("cpl-viewed-pets");

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("cpl-theme-v2", theme);
  }, [theme]);

  useEffect(() => setPage(1), [query, sortMode, selectedTags, selectedCollection]);

  useEffect(() => {
    let title = "Browse and Download Codex Pets | Codex Pet Lab";
    let description = "Browse installable V2 Codex pets with animated spritesheet previews, downloads, and one-command installation.";
    let canonicalPath = "/pets";
    if (contentPage) {
      title = contentPage.title;
      description = contentPage.description;
      canonicalPath = contentPage.path;
    } else if (routePet) {
      title = `${routePet.name} — Download Codex Pet | Codex Pet Lab`;
      description = routePet.description;
      canonicalPath = `/pets/${routePet.id}`;
    } else if (selectedCollection) {
      title = `${selectedCollection} Codex Pets | Codex Pet Lab`;
      description = `Browse animated ${selectedCollection} Codex pets with V2 previews, downloads, and installation commands.`;
      canonicalPath = `/collections/${toRouteSlug(selectedCollection)}`;
    } else if (selectedTags.length === 1) {
      title = `${selectedTags[0]} Codex Pets | Codex Pet Lab`;
      description = `Browse animated Codex pets tagged ${selectedTags[0]}.`;
      canonicalPath = `/tags/${toRouteSlug(selectedTags[0])}`;
    } else if (view === "collections") {
      title = "Codex Pet Collections | Codex Pet Lab";
      description = "Browse curated collections of animated V2 Codex pets.";
      canonicalPath = "/collections";
    } else if (view === "creators") {
      title = "Codex Pet Creators | Codex Pet Lab";
      description = "Browse creators publishing animated V2 Codex pets.";
      canonicalPath = "/creators";
    } else if (view === "upload") {
      title = "My Local Codex Pet Library | Codex Pet Lab";
      description = "Validate and manage Codex pets stored locally in this browser.";
      canonicalPath = "/library";
    } else if (view === "rank") {
      title = "AI Token Usage Leaderboard | Codex Pet Lab";
      description = "Report your AI coding-tool token usage (counts only) and unlock animated Codex pets by milestone and monthly season rank.";
      canonicalPath = "/rank";
    } else if (view === "download") {
      title = "Download Meterling — AI Usage Desktop Companion | Codex Pet Lab";
      description = "Download Meterling for macOS: a local-first AI usage desktop companion with Codex and Claude Code token stats, collectibles, and draw rewards.";
      canonicalPath = "/download";
    } else if (view === "play") {
      title = "Meterling — Desktop Companion Prototype";
      description = "A playable desktop companion prototype powered by effective AI token usage.";
      canonicalPath = "/play";
    }
    document.title = title;
    document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", description);
    document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute("href", `https://codexpetlab.com${canonicalPath}`);
  }, [contentPage, routePet, selectedCollection, selectedTags, view]);

  useEffect(() => {
    let generation = 0;
    const syncRoute = () => {
      const currentGeneration = ++generation;
      const hashMatch = window.location.hash.match(/^#\/pets\/([a-z0-9]+(?:-[a-z0-9]+)*)$/);
      if (hashMatch) window.history.replaceState({}, "", `/pets/${hashMatch[1]}`);
      const routePath = window.location.hash === "#/play" ? "/play" : window.location.pathname;
      const route = resolveEngineRoute(routePath);
      const nextContentPage = route.contentPath ? getSeoContentPage(route.contentPath) ?? null : null;
      setView(route.view);
      setRankSub(route.rankSub);
      const localPet = route.petId ? pets.find((pet) => pet.id === route.petId) ?? null : null;
      setRoutePet(localPet);
      if (route.petId && !localPet) {
        fetch(`/api/pets/${route.petId}/share-data`)
          .then(async (response) => response.ok ? response.json() as Promise<{ pet: MirroredShareData }> : null)
          .then((body) => { if (body && currentGeneration === generation) setRoutePet(mirroredShareDataToPet(body.pet)); })
          .catch(() => undefined);
      }
      setContentPage(nextContentPage);
      setInspectorPet(null);
      setSelectedCollection(route.collectionSlug ? allCollections.find((collection) => toRouteSlug(collection) === route.collectionSlug) : undefined);
      setSelectedTags(route.tag ? [allTags.find((tag) => toRouteSlug(tag) === route.tag) ?? route.tag] : []);
      setQuery(route.creatorHandle ?? "");
    };
    syncRoute();
    window.addEventListener("popstate", syncRoute);
    window.addEventListener("hashchange", syncRoute);
    return () => {
      window.removeEventListener("popstate", syncRoute);
      window.removeEventListener("hashchange", syncRoute);
    };
  }, []);

  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);

  useEffect(() => {
    if (view !== "gallery" || routePet || contentPage) return;
    const controller = new AbortController();
    setCatalogLoading(true);
    fetchCatalog({ page, pageSize: PAGE_SIZE, sort: sortMode, q: query }, controller.signal)
      .then((response) => setCatalog(response))
      .catch((error) => { if ((error as Error).name !== "AbortError") setCatalog({ pets: [], page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 1 }); })
      .finally(() => setCatalogLoading(false));
    return () => controller.abort();
  }, [view, routePet, contentPage, page, sortMode, query]);

  const navigateToView = (nextView: View) => {
    setRoutePet(null);
    setContentPage(null);
    setInspectorPet(null);
    setView(nextView);
    setRankSub(undefined);
    setSelectedCollection(undefined);
    setSelectedTags([]);
    setQuery("");
    window.history.pushState({}, "", getViewPath(nextView));
  };

  const navigateEngineItem = (item: typeof ENGINE_NAV_ITEMS[number]) => {
    navigateToView(item.view);
  };

  const viewTitleKey: Record<View, string> = {
    gallery: "nav.discover",
    creators: "nav.workshop",
    collections: "nav.collections",
    upload: "nav.library",
    rank: "nav.rank",
    download: "nav.download",
    play: "nav.rank",
  };

  const selectPet = (pet: CatalogPet) => {
    viewed.add(pet.id);
    const local = pets.find((item) => item.id === pet.id);
    if (local) { setInspectorPet(local); return; }
    fetch(`/api/pets/${pet.id}/share-data`)
      .then((response) => response.ok ? response.json() as Promise<{ pet: MirroredShareData }> : null)
      .then((body) => { if (body) setInspectorPet(mirroredShareDataToPet(body.pet)); })
      .catch(() => undefined);
  };

  const openPet = (pet: CatalogPet) => {
    viewed.add(pet.id);
    setInspectorPet(null);
    window.history.pushState({}, "", `/pets/${pet.id}`);
    const local = pets.find((item) => item.id === pet.id);
    if (local) { setRoutePet(local); return; }
    fetch(`/api/pets/${pet.id}/share-data`)
      .then((response) => response.ok ? response.json() as Promise<{ pet: MirroredShareData }> : null)
      .then((body) => { if (body) setRoutePet(mirroredShareDataToPet(body.pet)); })
      .catch(() => undefined);
  };

  const chooseCollection = (collection: string) => {
    setSelectedCollection(collection);
    setSelectedTags([]);
    setQuery("");
    setView("gallery");
    window.history.pushState({}, "", `/collections/${toRouteSlug(collection)}`);
  };

  const chooseCreator = (creator: string) => {
    setQuery(creator);
    setSelectedCollection(undefined);
    setSelectedTags([]);
    setView("gallery");
    window.history.pushState({}, "", `/creators/${toRouteSlug(creator)}`);
  };

  const sharePet = async (pet: Pet) => {
    const url = `${window.location.origin}/pets/${pet.id}`;
    if (navigator.share) {
      await navigator.share({ title: `${pet.name} — Codex Pet Lab`, text: pet.description, url });
      return;
    }
    await navigator.clipboard.writeText(url);
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((current) => (current.includes(tag) ? current.filter((item) => item !== tag) : [...current, tag]));
  };

  if (view === "play") return <GamePage />;

  return (
    <div className="app-shell">
      <header className="engine-topbar">
        <a className="engine-brand" href="/pets" onClick={(event) => { event.preventDefault(); navigateToView("gallery"); }}><Sparkles size={18} /><span>Codex Pet Lab</span></a>
        <nav className="engine-nav" aria-label="Main navigation">
          {ENGINE_NAV_ITEMS.map((item) => (
            <a
              key={item.label}
              href={item.path}
              className={view === item.view ? "active" : ""}
              onClick={(event) => { event.preventDefault(); navigateEngineItem(item); }}
            >
              {t(item.translationKey)}
            </a>
          ))}
        </nav>
        <div className="engine-actions"><span>{t("shell.readyPets", { count: pets.length })}</span><LanguageSwitcher /><button className="icon-button" type="button" title={theme === "dark" ? t("common.light") : t("common.dark")} onClick={() => setTheme(theme === "dark" ? "light" : "dark")} aria-label={theme === "dark" ? t("common.light") : t("common.dark")}>{theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}</button></div>
      </header>
      <aside className="rail" aria-label="Primary navigation">
        <a className="brand-mark" href="/pets" onClick={(event) => { event.preventDefault(); navigateToView("gallery"); }} aria-label="Codex Pet Lab home">
          <Sparkles size={22} />
        </a>
        <nav className="rail-nav">
          {navItems.map((item) => (
            <a
              key={item.view}
              href={getViewPath(item.view)}
              className={view === item.view ? "rail-button active" : "rail-button"}
              onClick={(event) => { event.preventDefault(); navigateToView(item.view); }}
              aria-label={item.view === "upload" ? t("nav.library") : item.view === "gallery" ? t("nav.discover") : item.view === "creators" ? t("nav.workshop") : item.view === "rank" ? t("nav.rank") : t("nav.collections")}
              title={item.view === "upload" ? t("nav.library") : item.view === "gallery" ? t("nav.discover") : item.view === "creators" ? t("nav.workshop") : item.view === "rank" ? t("nav.rank") : t("nav.collections")}
            >
              <item.icon size={19} />
            </a>
          ))}
        </nav>
      </aside>

      <header className="mobile-header">
        <button className="icon-button" type="button" onClick={() => setMobileNavOpen(true)} aria-label="Open navigation">
          <Menu size={20} />
        </button>
        <a className="mobile-brand" href="/pets" onClick={(event) => { event.preventDefault(); navigateToView("gallery"); }}>$ codex-pet-lab</a>
        <button className="icon-button" type="button" onClick={() => setTheme(theme === "light" ? "dark" : "light")} aria-label="Toggle theme">
          {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
        </button>
      </header>

      {mobileNavOpen && (
        <div className="mobile-drawer-backdrop" role="presentation" onClick={() => setMobileNavOpen(false)}>
          <nav className="mobile-drawer" aria-label="Mobile navigation" onClick={(event) => event.stopPropagation()}>
            <div className="mobile-drawer-title"><span>$ codex-pet-lab</span><button className="icon-button" type="button" onClick={() => setMobileNavOpen(false)} aria-label="Close navigation"><X size={20} /></button></div>
            {navItems.map((item) => (
              <a key={item.view} href={getViewPath(item.view)} className="mobile-nav-link" onClick={(event) => { event.preventDefault(); navigateToView(item.view); setMobileNavOpen(false); }}>
                <item.icon size={18} /> {item.view === "upload" ? t("nav.library") : item.view === "gallery" ? t("nav.discover") : item.view === "creators" ? t("nav.workshop") : item.view === "rank" ? t("nav.rank") : t("nav.collections")}
              </a>
            ))}
          </nav>
        </div>
      )}

      <main className={inspectorPet && !routePet && !contentPage && view === "gallery" ? "main-content with-inspector" : "main-content"}>
        {contentPage ? (
          <SeoContentPage page={contentPage} />
        ) : routePet ? (
          <PetDetailPage
            pet={routePet}
            liked={liked.values.has(routePet.id)}
            viewed={viewed.values.has(routePet.id)}
            onBack={() => {
              window.history.pushState({}, "", "/pets");
              setRoutePet(null);
            }}
            onLike={() => liked.toggle(routePet.id)}
            onShare={() => void sharePet(routePet)}
          />
        ) : (
          <>
            {view !== "upload" && view !== "download" && <div className="page-heading">
              <div>
                <p className="eyebrow">{t("gallery.eyebrow")}</p>
                <h1>{t(viewTitleKey[view])}</h1>
              </div>
              <div className="brand-wordmark">$ codex-pet-lab</div>
            </div>}

            {view === "gallery" && (
          <><DiscoverHighlights originalCount={pets.length}/><Gallery
            query={query}
            setQuery={setQuery}
            sortMode={sortMode}
            setSortMode={setSortMode}
            selectedTags={selectedTags}
            toggleTag={toggleTag}
            selectedCollection={selectedCollection}
            setSelectedCollection={setSelectedCollection}
            filtersOpen={filtersOpen}
            setFiltersOpen={setFiltersOpen}
            pets={catalog?.pets ?? []}
            total={catalog?.total ?? 0}
            page={catalog?.page ?? 1}
            pageCount={catalog?.totalPages ?? 1}
            loading={catalogLoading}
            setPage={setPage}
            liked={liked.values}
            viewed={viewed.values}
            toggleLike={liked.toggle}
            selectPet={selectPet}
            openPet={openPet}
              /></>
            )}
            {view === "collections" && <Collections onChoose={chooseCollection} />}
            {view === "creators" && <ExternalWorkshop />}
            {view === "upload" && <UploadPage />}
            {view === "rank" && <RankPage sub={rankSub} />}
            {view === "download" && <DownloadPage />}

            <footer className="site-footer">
              <div className="site-footer-copy"><span>Codex Pet Lab is an independent community experiment.</span><span>Not affiliated with or endorsed by OpenAI.</span></div>
              <nav className="seo-resource-links" aria-label="Codex Pet guides and tools">
                {SEO_CONTENT_PAGES.map((page) => <a key={page.path} href={page.path}>{page.heading}</a>)}
              </nav>
            </footer>
          </>
        )}
      </main>
      {inspectorPet && !routePet && !contentPage && view === "gallery" && <PetInspector pet={inspectorPet} liked={liked.values.has(inspectorPet.id)} onClose={() => setInspectorPet(null)} onLike={() => liked.toggle(inspectorPet.id)} onOpenDetails={() => openPet({ ...inspectorPet, origin: "original", posterUrl: "" })} />}
    </div>
  );
}

interface GalleryProps {
  query: string;
  setQuery: (value: string) => void;
  sortMode: SortMode;
  setSortMode: (value: SortMode) => void;
  selectedTags: string[];
  toggleTag: (tag: string) => void;
  selectedCollection?: string;
  setSelectedCollection: (collection: string | undefined) => void;
  filtersOpen: boolean;
  setFiltersOpen: (open: boolean) => void;
  pets: CatalogPet[];
  total: number;
  page: number;
  pageCount: number;
  loading: boolean;
  setPage: (page: number) => void;
  liked: Set<string>;
  viewed: Set<string>;
  toggleLike: (id: string) => void;
  selectPet: (pet: CatalogPet) => void;
  openPet: (pet: CatalogPet) => void;
}

function Gallery(props: GalleryProps) {
  const { t } = useTranslation();
  return (
    <>
      <section className="search-row" aria-label="Search pets">
        <label className="search-field">
          <Search size={19} />
          <input value={props.query} onChange={(event) => props.setQuery(event.target.value)} placeholder={t("gallery.search")} />
          {props.query && <button type="button" onClick={() => props.setQuery("")} aria-label="Clear search"><X size={16} /></button>}
        </label>
        <button className="primary-button search-button" type="button"><Search size={17} /> {t("gallery.find")}</button>
      </section>

      <section className="gallery-toolbar">
        <div className="sort-tabs" role="group" aria-label="Sort pets">
          {sortOptions.map((option) => (
            <button key={option.value} className={props.sortMode === option.value ? "active" : ""} type="button" onClick={() => props.setSortMode(option.value)}>{t(`gallery.${option.value}`)}</button>
          ))}
        </div>
        <button className={props.filtersOpen ? "secondary-button active" : "secondary-button"} type="button" onClick={() => props.setFiltersOpen(!props.filtersOpen)}>
          <Filter size={16} /> {t("gallery.filters")} {(props.selectedTags.length > 0 || props.selectedCollection) && <span className="filter-count">{props.selectedTags.length + (props.selectedCollection ? 1 : 0)}</span>}
        </button>
      </section>

      {props.filtersOpen && (
        <section className="filter-panel">
          <div className="filter-group"><span className="filter-label">{t("gallery.collection")}</span><div className="chip-row"><button className={!props.selectedCollection ? "chip selected" : "chip"} type="button" onClick={() => props.setSelectedCollection(undefined)}>{t("common.all")}</button>{allCollections.map((collection) => <button className={props.selectedCollection === collection ? "chip selected" : "chip"} type="button" key={collection} onClick={() => props.setSelectedCollection(collection)}>{collection}</button>)}</div></div>
          <div className="filter-group"><span className="filter-label">{t("gallery.tags")}</span><div className="chip-row">{allTags.map((tag) => <button className={props.selectedTags.includes(tag) ? "chip selected" : "chip"} type="button" key={tag} onClick={() => props.toggleTag(tag)}>{tag}</button>)}</div></div>
        </section>
      )}

      <div className="gallery-meta"><span>{t("gallery.pets", { count: props.total })}</span><span>{t("gallery.page", { page: props.page, total: props.pageCount })}</span></div>
      {props.pets.length > 0 ? (
        <section className="pet-grid" aria-live="polite">
          {props.pets.map((pet) => (
            <PetCard key={pet.id} pet={pet} liked={props.liked.has(pet.id)} viewed={props.viewed.has(pet.id)} onLike={() => props.toggleLike(pet.id)} onSelect={() => props.selectPet(pet)} onOpen={() => props.openPet(pet)} />
          ))}
        </section>
      ) : (
        <section className="empty-state"><Compass size={30} /><h2>{t("gallery.empty")}</h2><p>{t("gallery.emptyHint")}</p></section>
      )}
      <nav className="pagination" aria-label="Gallery pages">
        <button className="secondary-button" type="button" disabled={props.page === 1} onClick={() => props.setPage(props.page - 1)}><ChevronLeft size={16} /> {t("gallery.previous")}</button>
        <span>{props.page} of {props.pageCount}</span>
        <button className="secondary-button" type="button" disabled={props.page === props.pageCount} onClick={() => props.setPage(props.page + 1)}>{t("gallery.next")} <ChevronRight size={16} /></button>
      </nav>
    </>
  );
}

function PetCard({ pet, liked, viewed, onLike, onSelect, onOpen }: { pet: CatalogPet; liked: boolean; viewed: boolean; onLike: () => void; onSelect: () => void; onOpen: () => void }) {
  const { t } = useTranslation();
  const local = pet.origin === "original" ? pets.find((item) => item.id === pet.id) : undefined;
  const artSrc = local ? petSvgDataUri(local) : pet.posterUrl;
  const openDetails = (event: MouseEvent<HTMLAnchorElement>) => { event.preventDefault(); event.stopPropagation(); onOpen(); };
  const download = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (local) { downloadPetArchive(local); return; }
    window.location.href = `/api/pets/${pet.id}/download`;
  };
  return (
    <article className="pet-card" onClick={onSelect} onDoubleClick={onOpen} tabIndex={0} onKeyDown={(event) => { if (event.key === "Enter") onOpen(); else if (event.key === " ") onSelect(); }}>
      <div className="pet-art-wrap">
        <a className="pet-art-link" href={`/pets/${pet.id}`} onClick={openDetails} aria-label={`Open ${pet.name} details`}>
          <img src={artSrc} alt={`${pet.name}, ${pet.description}`} loading="lazy" />
        </a>
        <div className="card-badges"><span>{t("gallery.directions")}</span><span>{pet.species || "workshop"}</span></div>
      </div>
      <div className="pet-card-body">
        <div className="card-title-row"><h2><a href={`/pets/${pet.id}`} onClick={openDetails}>{pet.name}</a></h2><span className="mini-stat"><Eye size={13} /> {formatCount(pet.views + (viewed ? 1 : 0))}</span></div>
        <p className="creator-line">by <a href={`/creators/${toRouteSlug(pet.creatorHandle)}`} onClick={(event) => event.stopPropagation()}>@{pet.creatorHandle}</a></p>
        <p className="pet-description">{pet.description}</p>
        <div className="tag-list">{pet.tags.map((tag) => <a href={`/tags/${toRouteSlug(tag)}`} onClick={(event) => event.stopPropagation()} key={tag}>{tag}</a>)}</div>
      </div>
      <div className="card-actions">
        <button type="button" onClick={download}><Download size={15} /> {t("gallery.download")}</button>
        <button className={liked ? "liked" : ""} type="button" onClick={(event) => { event.stopPropagation(); onLike(); }}><Heart size={15} fill={liked ? "currentColor" : "none"} /> {pet.likes + (liked ? 1 : 0)}</button>
      </div>
    </article>
  );
}

function Collections({ onChoose }: { onChoose: (collection: string) => void }) {
  return <section className="collection-grid">{allCollections.map((collection, index) => { const members = pets.filter((pet) => pet.collection === collection); return <a className="collection-card" href={`/collections/${toRouteSlug(collection)}`} key={collection} onClick={(event) => { event.preventDefault(); onChoose(collection); }}><div className={`collection-stack stack-${index + 1}`}>{members.slice(0, 3).map((pet, petIndex) => <img key={pet.id} src={petSvgDataUri(pet)} alt="" style={{ transform: `translate(${petIndex * 18}px, ${petIndex * 5}px) rotate(${petIndex * 4 - 4}deg)` }} />)}</div><div><span className="eyebrow">Curated collection</span><h2>{collection}</h2><p>{members.length} original pets</p></div><ChevronRight size={20} /></a>; })}</section>;
}

function Creators({ onChoose }: { onChoose: (creator: string) => void }) {
  const creators = useMemo(() => [...new Map(pets.map((pet) => [pet.creatorHandle, { name: pet.creator, handle: pet.creatorHandle, pets: pets.filter((item) => item.creatorHandle === pet.creatorHandle) }])).values()], []);
  return <section className="creator-grid">{creators.map((creator) => <a className="creator-card" href={`/creators/${toRouteSlug(creator.handle)}`} key={creator.handle} onClick={(event) => { event.preventDefault(); onChoose(creator.handle); }}><img src={petSvgDataUri(creator.pets[0])} alt="" /><div><h2>{creator.name}</h2><p>@{creator.handle}</p></div><span>{creator.pets.length} pet{creator.pets.length === 1 ? "" : "s"}</span></a>)}</section>;
}

export default App;
